import React, { FC, useEffect, useState } from 'react'

import { styled } from '@mui/material/styles'

import { Button, DateField, SimpleShowLayout, useGetOne, useDataProvider, useRefresh, useRedirect, useNotify, Confirm } from 'react-admin'
import CloseIcon from '@mui/icons-material/Close'
import Drawer from '@mui/material/Drawer'

import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';

import { AttributesTable } from 'shared'
import { DataSummaryField } from './DataSummaryField'
import { parseURLParams } from 'shared/helpers'
import { timeAgo } from 'shared/atoms/time_ago'

// import { ElementSansParentProps } from 'dash/shared';
import { Entry } from 'shared/types'

const ITEM_HEIGHT = 48;

export default function ActionsMenu({ closeDrawer, entryId }) {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  const [activeAction, setActiveAction] = React.useState<string>(null)
  const [confirmfOpen, setConfirmOpen] = React.useState<boolean>(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDelete = async () => {
    try {
      await dataProvider.delete('Entry', { id: entryId })
      notify('Entry was deleted.', { type: 'success' })
      closeDrawer()
      refresh()
    } catch (e) {
      console.error(e)
      notify('There was an error deleting the entry.', { type: 'error' })
    }
  }

  const actions = {
    'Delete': {
      title: 'Delete Entry',
      content: 'Are you sure you want to delete this entry? This action cannot be undone.',
      action: onDelete
    },
  }

  React.useEffect(() => {
    if (activeAction) setConfirmOpen(true)
    else setConfirmOpen(false)
  }, [activeAction])

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 4.5,
            width: '20ch',
          },
        }}
      >
        {Object.keys(actions).map((action) => (
          <MenuItem key={action} onClick={() => {
            setActiveAction(action)
            handleClose()
          }}>
            {action}
          </MenuItem>
        ))}
      </Menu>
      {activeAction && <Confirm
        isOpen={confirmfOpen}
        title={actions[activeAction].title}
        content={actions[activeAction].content}
        onConfirm={() => {
          setActiveAction(null)
          actions[activeAction].action()
        }}
        onClose={() => setActiveAction(null)}
      />}
    </div>
  );
}

const PREFIX = 'EntryQuickPreviewDrawer'

const classes = {
  entryPreview: `${PREFIX}-entryPreview`,
}

const Container = styled(Drawer)({
  '@media(min-width: 720px)': {
    [`& .${classes.entryPreview}`]: {
      width: '100% !important',
    },
  },
  '@media(min-width: 380px)': {
    [`& .${classes.entryPreview}`]: {
      width: '100vw',
    },
  }
})

type EntryQuickPreviewDrawerProps = {
  displayedKeys: string[]
  onClose: () => void
  record: string;
  showPanel: boolean
}

export const EntryQuickPreviewDrawer: FC<EntryQuickPreviewDrawerProps> = ({ displayedKeys, onClose, record: id, showPanel }) => {
  // const id = useRecordContext()
  if (!id) return null

  let { data, isLoading } = useGetOne('Entry', { id })

  if (isLoading) return null

  const meta = data.humanReadableInternalMeta

  return (
    <Container
      anchor="right"
      PaperProps={{
        style: {
          minWidth: '60vw',
          backgroundColor: '#ebebeb'
        },
      }}
      open={showPanel}
      onClose={onClose}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 16, borderBottom: '1px lightgrey solid' }}>
        <div style={{ width: 100 }}>
          <Button
            label="Close"
            onClick={onClose}>
            <CloseIcon />
          </Button>
        </div>
        <div>
          <strong>Form Entry {id.split('-')[0]}</strong>
        </div>
        <div style={{ width: 100, textAlign: 'right' }}>
          <ActionsMenu entryId={id} closeDrawer={onClose} />
        </div>
      </div>
      <div className={classes.entryPreview}>
        <SimpleShowLayout record={data}>
          <Card>
            <CardHeader
              // avatar={
              //   <Avatar sx={{ bgcolor: red[500] }} aria-label="recipe">
              //     R
              //   </Avatar>
              // }
              // action={
              //   <IconButton aria-label="settings">
              //     <MoreVertIcon />
              //   </IconButton>
              // }
              subheader="DATA"
            />
            <CardContent>
              <DataSummaryField copyable source="data" displayedKeys={displayedKeys} limit={100} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader subheader="METADATA" />
            <CardContent>
              {meta && <AttributesTable
                copyable
                tClass="formtopia-entry-attrs"
                data={{
                  ...meta,
                  spam: meta.spam?.is_spam ? "⚠ LIKELY Spam" : "✓ UNLIKELY Spam",
                  'spam score': meta.spam?.score,
                  created: timeAgo(data.createdAt),
                }}
                displayedKeys={['country', 'language', 'source', 'spam', 'spam score', 'created']}
                flatten={false}
              />}
            </CardContent>
          </Card>
        </SimpleShowLayout>
      </div>
    </Container>
  )
}
